/** Right-side drawer for one external-ID mapping — view and edit.
 *
 * Deep-linked at /ext-ids/:id (composite id, e.g. `aeid:1234`), so closing it
 * restores the grid's exact search and the open panel is shareable.
 *
 * The read view leads with the three things no console surface has ever shown:
 * the per-pipeline `used_by` verdicts with their reasons, the tags applied to a
 * participant on match, and the fan-out targets that silently add a booking to
 * further tours.
 *
 * Editing is gated on `console_ext_ids_manage` and on the kind: an alias is
 * fully editable, a primary offers only its value (its column has no companion
 * fields) and cannot be deleted from here at all.
 *
 * And on the owner: an import-owned (managed) alias is kept in step by its import,
 * so it is read-only until taken over ("Take over…"); an ops-owned alias can be
 * handed back ("Hand back to import…"). A deep link into the form on a managed row
 * opens it disabled, with the reason and the way out.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as extIdsApi from '@/api/extIds';
import type { ExtIdDetail, ExtIdFacet } from '@/api/extIds';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { ExtIdFields, EMPTY_FORM, type ExtIdFormValue } from './ExtIdFields';
import { KindChip, ManagedChip, ModelChip, ParkedChip, managedSource, mono } from './extIdFormat';
import { fieldErrorsOf, managedRefusalOf, messageOf } from './extIdErrors';
import {
  HAND_BACK_LABEL,
  MANAGED_DELETE_REASON,
  MANAGED_EDIT_REASON,
  TAKE_OVER_LABEL,
  ownershipActionOf,
} from './ownership';
import { useExtIdOwnership } from './useExtIdOwnership';
import {
  danglingActions,
  facetActions,
  fanoutTargetActions,
  idTypeActions,
  integrationActions,
  kindActions,
  modelActions,
  parkedActions,
  rowIdActions,
  tagActions,
  targetActions,
  targetHrefFor,
  tenantActions,
  valueActions,
  type ExtIdCtx,
  type ExtIdFacetKey,
} from './extIdLinks';

interface Props {
  extId: string;
  open: boolean;
  onClose: () => void;
  /** Build the drawer route for a sibling, preserving the grid's search. */
  detailPath: (id: string) => string;
  /** Open straight into the form (the grid's "Edit…" kebab action). */
  startEditing?: boolean;
  /** Called when the form is left, so the caller can drop `?edit=1`. */
  onEditingDone?: () => void;
}

const FACET_LABEL: Record<string, string> = {
  import: 'XML import',
  bookings: 'Booking sync',
  api: 'Vendor API',
};

const FACET_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  yes: 'success',
  no: 'error',
  na: 'default',
};

function toForm(d: ExtIdDetail): ExtIdFormValue {
  return {
    custom_id: d.value,
    id_type: d.id_type ?? 'product_id',
    use_for_matching: d.use_for_matching,
    api_queryable: d.api_queryable,
    integration: d.integration ?? '',
    restech_account: d.restech_account ?? '',
    comment: d.comment ?? '',
    ext_link: d.ext_link ?? '',
    tour: { id: d.object_id, name: d.target.name ?? `#${d.object_id}` },
    tags: d.tags.map((t) => ({ id: t.id, name: t.name, category: t.category })),
    targets: d.fanout_targets.map((t) => ({ id: t.id, name: t.name })),
  };
}

export function ExtIdDetailDrawer({
  extId,
  open,
  onClose,
  detailPath,
  startEditing = false,
  onEditingDone,
}: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const perms = usePermissions();
  const canManage = perms.hasPerm('console_ext_ids_manage');

  const [editing, setEditing] = useState(startEditing);
  const [form, setForm] = useState<ExtIdFormValue>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ownership = useExtIdOwnership();

  const detailQuery = useQuery({
    queryKey: ['ext-ids', 'detail', extId],
    queryFn: () => extIdsApi.retrieve(extId),
    enabled: open,
  });
  const d: ExtIdDetail | undefined = detailQuery.data;
  const ctx: ExtIdCtx = { tenantId: d?.tenant.id ?? null };
  /** Import-owned: read-only until taken over (the server refuses anything else). */
  const managed = d?.managed === true;
  const ownershipAction = d ? ownershipActionOf(d) : null;

  // A different row (or a fresh open) always starts in read mode — carrying an
  // edit across rows would apply one mapping's values to another.
  useEffect(() => {
    setEditing(startEditing);
    setErrors({});
  }, [extId, open, startEditing]);

  useEffect(() => {
    if (d) setForm(toForm(d));
  }, [d]);

  const save = useMutation({
    mutationFn: () => {
      if (!d) throw new Error('not loaded');
      if (d.kind === 'primary') {
        return extIdsApi.update(d.id, { custom_id: form.custom_id });
      }
      return extIdsApi.update(d.id, {
        custom_id: form.custom_id,
        id_type: form.id_type,
        use_for_matching: form.use_for_matching,
        api_queryable: form.api_queryable,
        integration: form.integration.trim() || null,
        restech_account: form.restech_account.trim() || null,
        comment: form.comment.trim() || null,
        ext_link: form.ext_link.trim() || null,
        ...(form.tour && form.tour.id !== d.object_id ? { tour_id: form.tour.id } : {}),
        tag_ids: form.tags.map((t) => t.id),
        target_ids: form.targets.map((t) => t.id),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show('Mapping saved.', 'success');
      setEditing(false);
      setErrors({});
      onEditingDone?.();
    },
    onError: (e) => {
      // The row went to an import since it loaded: reload it, and the form turns
      // read-only with the way out.
      if (managedRefusalOf(e)) void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      const fields = fieldErrorsOf(e);
      setErrors(fields);
      if (Object.keys(fields).length === 0) {
        snackbar.show(messageOf(e, 'Could not save the mapping.'), 'error');
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => extIdsApi.remove(extId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show('Mapping deleted.', 'success');
      setConfirmDelete(false);
      onClose();
    },
    onError: (e) => {
      setConfirmDelete(false);
      if (managedRefusalOf(e)) void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show(messageOf(e, 'Could not delete the mapping.'), 'error');
    },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 580, p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {editing ? 'Edit mapping' : 'External ID'}
          </Typography>
          {d && !editing && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {managed && <ManagedChip integration={d.integration} restechAccount={d.restech_account} />}
              <ValueActions chip actions={kindActions(d.kind)}>
                <KindChip kind={d.kind} />
              </ValueActions>
            </Stack>
          )}
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {detailQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {detailQuery.isError && <Alert severity="error">Couldn’t load this mapping.</Alert>}

        {d && editing && (
          <Stack spacing={2}>
            {d.kind === 'primary' && (
              <Alert severity="warning">
                Changing a tour&apos;s own external ID affects how imports resolve it. Existing
                bookings keep their mapping; future imports must send the new value.
              </Alert>
            )}
            {managed && (
              <Alert
                severity="info"
                data-managed-lock
                action={
                  canManage ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => ownership.request(d)}
                      disabled={ownership.isPending}
                    >
                      {TAKE_OVER_LABEL}
                    </Button>
                  ) : undefined
                }
              >
                Maintained by an import ({managedSource(d.integration, d.restech_account)}): its
                syncs update this ID, so it is read-only here. Take it over to edit it.
              </Alert>
            )}
            <ExtIdFields
              value={form}
              onChange={setForm}
              tenantId={d.tenant.id}
              kind={d.kind}
              errors={errors}
              disabled={save.isPending || managed}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={() => {
                  setEditing(false);
                  setErrors({});
                  setForm(toForm(d));
                  onEditingDone?.();
                }}
                disabled={save.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => save.mutate()}
                disabled={save.isPending || managed || !form.custom_id.trim()}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        )}

        {d && !editing && (
          <Stack spacing={2}>
            <Field label="Value">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ValueActions actions={valueActions(d.value, ctx)}>
                  <Box component="code" sx={mono}>
                    {d.value}
                  </Box>
                </ValueActions>
                <CopyButton value={d.value} />
                {d.use_for_matching ? (
                  <ParkedChip marker={d.parked_marker} useForMatching={d.use_for_matching} />
                ) : (
                  <ValueActions chip actions={parkedActions()}>
                    <ParkedChip marker={d.parked_marker} useForMatching={d.use_for_matching} />
                  </ValueActions>
                )}
              </Stack>
              {d.value !== d.value.trim() && (
                <Typography variant="caption" color="warning.main">
                  This value has surrounding whitespace. Matching strips it, the uniqueness check
                  does not — so it can coexist with the trimmed form and shadow it.
                </Typography>
              )}
            </Field>

            <Field label="Maps to">
              <Stack direction="row" spacing={1} alignItems="center">
                <ValueActions chip actions={modelActions(d.model)}>
                  <ModelChip model={d.model} />
                </ValueActions>
                {d.is_dangling ? (
                  <ValueActions actions={danglingActions(d.object_id)}>
                    <Typography variant="body2" color="error.main" component="span">
                      missing entity #{d.object_id}
                    </Typography>
                  </ValueActions>
                ) : (
                  <ValueActions actions={targetActions(d.model, d.object_id, d.target.name)}>
                    <Link href={targetHref(d)} target="_blank" rel="noopener">
                      {d.target.name ?? `#${d.object_id}`}{' '}
                      <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  </ValueActions>
                )}
              </Stack>
            </Field>

            <Field label="Tenant">
              <ValueActions actions={tenantActions(d.tenant)}>{d.tenant.name}</ValueActions>
            </Field>

            {d.kind === 'alias' && (
              <Field label="Maintained by">
                {managed
                  ? `An import (${managedSource(d.integration, d.restech_account)}) — it updates this ID on every sync, so it is read-only here until taken over.`
                  : 'Ops — no import changes or removes it.'}
              </Field>
            )}

            {d.kind === 'alias' && (
              <Stack direction="row" spacing={3}>
                <Field label="ID type">
                  {d.id_type ? (
                    <ValueActions actions={idTypeActions(d.id_type)}>{d.id_type}</ValueActions>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="Integration">
                  {d.integration ? (
                    <ValueActions actions={integrationActions(d.integration, d.restech_account, ctx)}>
                      {d.restech_account ? `${d.integration} / ${d.restech_account}` : d.integration}
                    </ValueActions>
                  ) : (
                    'any'
                  )}
                </Field>
              </Stack>
            )}

            {(d.comment || d.ext_link) && (
              <>
                {d.comment && <Field label="Comment">{d.comment}</Field>}
                {d.ext_link && (
                  <Field label="External link">
                    <Link href={d.ext_link} target="_blank" rel="noopener noreferrer">
                      {d.ext_link}{' '}
                      <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  </Field>
                )}
              </>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Used by
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Each pipeline is judged on its own. “n/a” means this tenant does not run that
                pipeline at all — not that anything is wrong.
              </Typography>
              <Stack spacing={1}>
                {(['import', 'bookings', 'api'] as const).map((key) => (
                  <FacetRow key={key} facet={key} name={FACET_LABEL[key]!} value={d.used_by?.[key]} />
                ))}
              </Stack>
            </Box>

            {d.kind === 'alias' && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags applied on match
                  </Typography>
                  {d.tags.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None.
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {d.tags.map((t) => (
                        <ValueActions key={t.id} chip actions={tagActions(t, ctx)}>
                          <Chip
                            size="small"
                            label={t.category ? `${t.category}: ${t.name}` : t.name}
                          />
                        </ValueActions>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Also books onto
                  </Typography>
                  {d.fanout_targets.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nothing — a booking matching this ID joins one tour only.
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        color="warning.main"
                        display="block"
                        sx={{ mb: 1 }}
                      >
                        A booking matching this ID is ALSO added to these tours. Deleting this
                        mapping stops that happening.
                      </Typography>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {d.fanout_targets.map((t) => (
                          <ValueActions key={t.id} chip actions={fanoutTargetActions(t)}>
                            <Chip
                              size="small"
                              color="warning"
                              label={t.name}
                              component="a"
                              href={`/tours/${t.id}`}
                              target="_blank"
                              rel="noopener"
                              clickable
                            />
                          </ValueActions>
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              </>
            )}

            {d.siblings.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Others with this value
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Matched case- and whitespace-insensitively, which is how a near-duplicate
                    shows up.
                  </Typography>
                  <Stack spacing={0.5}>
                    {d.siblings.map((s) => (
                      <Stack key={s.id} direction="row" spacing={1} alignItems="center">
                        <KindChip kind={s.kind} />
                        {/* The value opens that row's own drawer (same tab, so
                            back returns here); the tour opens in a new tab so
                            the comparison is not lost. */}
                        <Link
                          component={RouterLink}
                          to={detailPath(s.id)}
                          sx={mono}
                          title="Open this mapping"
                        >
                          {s.value}
                        </Link>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          →
                        </Typography>
                        <Link
                          // Siblings can map onto a different model than this
                          // row (an itinerary or activity that shares the
                          // value) — route by THEIR model, never assume a tour.
                          href={targetHrefFor(s.model, s.target.id)}
                          target="_blank"
                          rel="noopener"
                          variant="body2"
                          noWrap
                          title={`Open the ${s.model} in a new tab`}
                        >
                          {s.target.name ?? `#${s.target.id}`}{' '}
                          <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                        </Link>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </>
            )}

            <Divider />
            <Field label="Row id">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ValueActions actions={rowIdActions(d.id)}>
                  <Box component="code" sx={mono}>
                    {d.id}
                  </Box>
                </ValueActions>
                <CopyButton value={d.id} />
              </Stack>
            </Field>

            {d.kind === 'alias' && perms.hasPerm('console_audit_view') && (
              <>
                <Divider />
                <Typography variant="subtitle2">Change history</Typography>
                <HistoryPanel
                  contentType="demo.additional_ext_id"
                  objectPk={d.id.split(':')[1] ?? ''}
                  onRestored={() => {
                    void detailQuery.refetch();
                    void qc.invalidateQueries({ queryKey: ['ext-ids'] });
                  }}
                />
              </>
            )}

            <PermissionGate perm="console_ext_ids_manage">
              <Stack direction="row" spacing={1}>
                {managed ? (
                  <Tooltip title={MANAGED_EDIT_REASON}>
                    <span>
                      <Button size="small" startIcon={<EditIcon />} disabled>
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditing(true)}
                    disabled={!canManage}
                  >
                    Edit
                  </Button>
                )}
                {ownershipAction && (
                  <Button
                    size="small"
                    startIcon={ownershipAction === 'take_over' ? <LockOpenIcon /> : <SyncIcon />}
                    onClick={() => ownership.request(d)}
                    disabled={!canManage || ownership.isPending}
                  >
                    {ownershipAction === 'take_over' ? TAKE_OVER_LABEL : HAND_BACK_LABEL}
                  </Button>
                )}
                {d.kind === 'alias' && !managed ? (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      managed
                        ? MANAGED_DELETE_REASON
                        : "A tour's own external ID lives on the tour — clear it there."
                    }
                  >
                    <span>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} disabled>
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </PermissionGate>
          </Stack>
        )}
      </Box>

      {/* Sibling of the Box, not a child: a Dialog nested inside a Drawer
          renders aria-hidden. */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this mapping?"
        message={
          d && d.fanout_targets.length > 0
            ? `Bookings for this external ID will stop matching, and will no longer be added to ${d.fanout_targets.length} further tour(s).`
            : 'Bookings for this external ID may stop matching a tour.'
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
      {ownership.dialog}
    </Drawer>
  );
}

function targetHref(d: ExtIdDetail): string {
  return targetHrefFor(d.model, d.object_id);
}

function FacetRow({
  facet,
  name,
  value,
}: {
  facet: ExtIdFacetKey;
  name: string;
  value: ExtIdFacet | undefined;
}) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <ValueActions chip actions={facetActions(facet, value.state)}>
        <Chip
          size="small"
          color={FACET_COLOR[value.state] ?? 'default'}
          variant={value.state === 'na' ? 'outlined' : 'filled'}
          label={value.state === 'na' ? 'n/a' : value.state}
          sx={{ minWidth: 52 }}
        />
      </ValueActions>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2">{name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {value.detail ?? value.reason}
        </Typography>
      </Box>
    </Stack>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function CopyButton({ value }: { value: string }) {
  const snackbar = useSnackbar();
  return (
    <Tooltip title="Copy">
      <IconButton
        size="small"
        aria-label="Copy"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          snackbar.show('Copied.', 'success');
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}
